import { Box, Divider, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { _ } from '/@/locale';
import { formatDate } from '/@/utils';
import type { HybridPhenology } from './use-phenology';

/** One colour per leg of the cycle, matching the axis and the legend. */
const LEG_COLORS = {
    emergence: '#97723A',
    flowering: '#A9CF80',
    maturity: '#006A28',
} as const;

const CRITICAL_COLOR = '#B85302';
/** The rail outside the cycle — Secondary at 12%, as in the design. */
const RAIL_COLOR = 'rgba(16,56,79,0.12)';

const STAGES = [
    { key: 'planting', label: () => _('Siembra'), dot: LEG_COLORS.emergence },
    { key: 'emergence', label: () => _('Emergencia'), dot: LEG_COLORS.flowering },
    { key: 'flowering', label: () => _('Floración'), dot: LEG_COLORS.maturity },
    { key: 'maturity', label: () => _('Madurez'), dot: LEG_COLORS.maturity },
] as const;

/** The three legs of the cycle; the axis, the GDU chips and the dates all lay out from this. */
const LEGS = [
    { from: 'planting', to: 'emergence', color: LEG_COLORS.emergence },
    { from: 'emergence', to: 'flowering', color: LEG_COLORS.flowering },
    { from: 'flowering', to: 'maturity', color: LEG_COLORS.maturity },
] as const;

type StageKey = (typeof STAGES)[number]['key'];
type LegKey = (typeof LEGS)[number]['to'];

const STAGE_BY_KEY = Object.fromEntries(STAGES.map((stage) => [stage.key, stage])) as Record<
    StageKey,
    (typeof STAGES)[number]
>;

// `formatDate`'s dayMonthOnly branch drops the zero padding on some locales, and these read
// as a column of dates, so they're formatted directly.
const shortDate = (value?: string) => (value ? dayjs(value).format('DD/MM') : '-');
/** e.g. "3 dic – 2 ene" */
const criticalRange = (start: string, end: string) =>
    `${formatDate(new Date(start), { format: 'short' })} – ${formatDate(new Date(end), { format: 'short' })}`;

function StageDot({ color }: { color: string }) {
    return (
        <Box
            sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: color,
                flexShrink: 0,
                boxShadow: '0.5px 0.5px 4px -2px black',
                border: 'solid 1px white',
            }}
        />
    );
}

const LABEL_COLUMN = 130;
/** Blank column between the hybrid names and the start of the axis. */
const LEAD_COLUMN = 40;
/** Room past the axis for the closing stage's name and date. */
const TRAILING_COLUMN = 130;
/**
 * Relative width of each leg, taken from the design (100 : 260 : 260).
 *
 * Deliberately *not* a scale of each leg's duration: planting→emergence is days against
 * months for the others, so a true scale collapses it and its labels pile up. Fixed shares
 * keep every column wide enough for the date, the critical-period range and the GDU chip it
 * has to hold, whatever the hybrids' actual calendars are.
 */
const LEG_FLEX = [1, 2.6, 2.6];

/** One column set shared by the rail and every hybrid row, so the dates stay under their stage. */
const GRID_COLUMNS = [
    `${LABEL_COLUMN}px`,
    `${LEAD_COLUMN}px`,
    ...LEG_FLEX.map((flex) => `minmax(0, ${flex}fr)`),
    `${TRAILING_COLUMN}px`,
].join(' ');

/** Rail height, shared by the coloured legs and the grey run-in/run-out. */
const RAIL_HEIGHT = 4;

/**
 * A stage's position is its grid column's edge; the rail's thumb and every row's dot centre on
 * it, or they drift apart by half a thumb. The thumb centres with a transform; a row's dot
 * leads a line of text, so its column is pulled half a dot left instead.
 */
const DOT_SIZE = 8;
const DOT_ALIGN = `-${DOT_SIZE / 2}px`;

/** The rail: three coloured legs over a full-width grey line, a thu spacing={2}mb at each boundary. */
function TimelineTrack({ hybrids }: { hybrids: HybridPhenology[] }) {
    // Every hybrid flowers at a slightly different point; the shared marker uses the first.
    const criticalPeriod = hybrids.find((hybrid) => hybrid.criticalPeriod)?.criticalPeriod;

    return (
        <Box display='grid' gridTemplateColumns={GRID_COLUMNS} alignItems='center' pt={4} pb={3}>
            {/* One continuous rail; the cycle only colours the middle of it. */}
            <Box
                sx={{
                    gridRow: 1,
                    gridColumn: '1 / -1',
                    height: RAIL_HEIGHT,
                    borderRadius: RAIL_HEIGHT / 2,
                    backgroundColor: RAIL_COLOR,
                }}
            />
            {LEGS.map((leg, index) => (
                <Box
                    key={leg.to}
                    sx={{
                        gridRow: 1,
                        // Columns 1 and 2 are the label and the lead-in.
                        gridColumn: index + 3,
                        height: RAIL_HEIGHT,
                        backgroundColor: leg.color,
                        position: 'relative',
                    }}
                >
                    <TrackThumb label={STAGE_BY_KEY[leg.from].label()} color={leg.color} />
                    {leg.to === 'flowering' && criticalPeriod && <CriticalPeriodMarker />}
                    {index === LEGS.length - 1 && (
                        <TrackThumb label={STAGE_BY_KEY[leg.to].label()} color={leg.color} atEnd />
                    )}
                </Box>
            ))}
        </Box>
    );
}

function TrackThumb({ label, color, atEnd }: { label: string; color: string; atEnd?: boolean }) {
    return (
        <Box
            sx={{
                position: 'absolute',
                top: '50%',
                // Centred on the boundary the stage sits at — the start of its leg, or the end
                // of the last one — which is the same point each row's dot is centred on.
                ...(atEnd ? { right: 0 } : { left: 0 }),
                transform: atEnd ? 'translate(50%, -50%)' : 'translate(-50%, -50%)',
            }}
        >
            <Typography
                variant='caption'
                color='primary'
                sx={{ position: 'absolute', bottom: 16, left: 0, whiteSpace: 'nowrap' }}
            >
                {label}
            </Typography>
            <Box
                sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: color,
                    border: '2px solid',
                    borderColor: 'background.paper',
                    boxShadow: '0.5px 0.5px 4px -2px black',
                }}
            />
        </Box>
    );
}

/** The box drawn around flowering on the rail, labelled underneath. */
function CriticalPeriodMarker() {
    return (
        <Box
            sx={{
                position: 'absolute',
                top: '50%',
                left: '100%',
                transform: 'translate(-50%, -50%)',
                width: 68,
                height: 20,
                borderRadius: '4px',
                border: '2px solid',
                borderColor: CRITICAL_COLOR,
                backgroundColor: 'rgba(184,83,2,0.25)',
            }}
        >
            <Typography
                variant='caption'
                sx={{
                    position: 'absolute',
                    top: 24,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                    color: CRITICAL_COLOR,
                }}
            >
                {_('Período crítico')}
            </Typography>
        </Box>
    );
}

/** One hybrid, its dates aligned to the shared axis. */
function HybridRow({ hybrid }: { hybrid: HybridPhenology }) {
    return (
        <Box display='grid' gridTemplateColumns={GRID_COLUMNS} alignItems='start'>
            <Stack pr={1}>
                <Stack direction='row' spacing={1} alignItems='center'>
                    <StageDot color={hybrid.color} />
                    <Typography variant='subtitle1' fontWeight={700} color='primary'>
                        {hybrid.catalogName}
                    </Typography>
                </Stack>
                {hybrid.totalGdus !== undefined && (
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {_('{{total}} total GDUs', { total: hybrid.totalGdus })}
                    </Typography>
                )}
            </Stack>

            <Box />
            {LEGS.map((leg) => (
                <Box key={leg.to} sx={{ pr: 0.5 }}>
                    <Stack direction='row' spacing={1} alignItems='center' ml={DOT_ALIGN}>
                        <StageValue
                            date={hybrid[leg.from]}
                            color={STAGE_BY_KEY[leg.from].dot}
                            critical={leg.from === 'flowering' ? hybrid.criticalPeriod : undefined}
                        />
                        {/* V6 flows after the emergence date rather than taking a thumb or a
                            column, which keeps it out of the alignment geometry. */}
                        {leg.from === 'emergence' && hybrid.v6 && (
                            <StageValue date={hybrid.v6} color={LEG_COLORS.flowering} label={_('V6')} />
                        )}
                    </Stack>
                    {hybrid.gdus[leg.to as LegKey] !== undefined && (
                        <Typography
                            variant='caption'
                            color='primary.mui.inactive'
                            sx={{
                                display: 'block',
                                mt: 0.5,
                                mx: 'auto',
                                width: '100%',
                                backgroundColor: (theme) => theme.palette.grey[100],
                                borderRadius: '4px',
                                px: 1,
                                py: 0.25,
                                whiteSpace: 'nowrap',
                                textAlign: 'center',
                            }}
                        >
                            {_('{{gdus}} GDUs', { gdus: hybrid.gdus[leg.to as LegKey] })}
                        </Typography>
                    )}
                </Box>
            ))}
            {/* The closing stage has no leg, so it lives in the trailing column. */}
            <Box ml={DOT_ALIGN}>
                <StageValue date={hybrid.maturity} color={STAGE_BY_KEY.maturity.dot} />
            </Box>
        </Box>
    );
}

function StageValue({
    date,
    color,
    critical,
    label,
}: {
    date?: string;
    color: string;
    critical?: { start: string; end: string };
    /** Names the marker before its date. Labelled markers read lighter than the stage dates. */
    label?: string;
}) {
    return (
        <Stack direction='row' spacing={0.5} alignItems='center' sx={{ whiteSpace: 'nowrap' }}>
            <StageDot color={color} />
            <Typography variant='body2' fontWeight={label ? undefined : 700} color='primary'>
                {label ? `${label} ${shortDate(date)}` : shortDate(date)}
            </Typography>
            {critical && (
                <Typography variant='body2' sx={{ color: CRITICAL_COLOR }}>
                    {' '}
                    {_('PC {{range}}', { range: criticalRange(critical.start, critical.end) })}
                </Typography>
            )}
        </Stack>
    );
}

/** Desktop: a fixed stage axis with one aligned row per hybrid. */
function WideTimeline({ hybrids }: { hybrids: HybridPhenology[] }) {
    return (
        // The columns have a floor below which the dates and the critical-period range stop
        // fitting; past that the grid scrolls rather than spilling out of the card.
        <Box sx={{ overflowX: 'auto' }}>
            <Stack minWidth={820} spacing={2}>
                <TimelineTrack hybrids={hybrids} />
                <Stack spacing={3}>
                    {hybrids.map((hybrid) => (
                        <HybridRow key={hybrid.id} hybrid={hybrid} />
                    ))}
                </Stack>
            </Stack>
        </Box>
    );
}

/** Phone: one card per hybrid, stages as a four-column strip. */
function CompactTimeline({ hybrids }: { hybrids: HybridPhenology[] }) {
    return (
        <Stack spacing={2}>
            {hybrids.map((hybrid) => (
                <Stack
                    key={hybrid.id}
                    spacing={1}
                    p={2}
                    sx={{
                        borderRadius: '4px',
                        border: '1px solid',
                        borderColor: (theme) => theme.palette.divider,
                    }}
                >
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <StageDot color={hybrid.color} />
                        <Typography variant='subtitle1' fontWeight={700} color='primary'>
                            {hybrid.catalogName}
                        </Typography>
                    </Stack>

                    <Box display='grid' gridTemplateColumns='repeat(4, 1fr)' gap={1}>
                        {STAGES.map((stage) => (
                            <Stack key={stage.key}>
                                <Typography variant='caption' color='primary.mui.inactive'>
                                    {stage.label()}
                                </Typography>
                                <Typography variant='body2' color='primary'>
                                    {shortDate(hybrid[stage.key])}
                                </Typography>
                            </Stack>
                        ))}
                    </Box>

                    {hybrid.criticalPeriod && (
                        <>
                            <Divider />
                            <Typography variant='body2' fontWeight={700} sx={{ color: CRITICAL_COLOR }}>
                                {_('Período crítico: {{range}}', {
                                    range: criticalRange(hybrid.criticalPeriod.start, hybrid.criticalPeriod.end),
                                })}
                            </Typography>
                        </>
                    )}
                </Stack>
            ))}
        </Stack>
    );
}

/** Legend naming each leg of the cycle by colour. */
export function PhenologyLegend() {
    const legs = [
        { color: LEG_COLORS.emergence, label: _('Siembra a emergencia') },
        { color: LEG_COLORS.flowering, label: _('Emergencia a floración') },
        { color: LEG_COLORS.maturity, label: _('Floración a Madurez fisiológica') },
    ];
    return (
        <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap>
            {legs.map((leg) => (
                <Stack key={leg.label} direction='row' spacing={0.5} alignItems='center'>
                    <StageDot color={leg.color} />
                    <Typography variant='caption' color='primary'>
                        {leg.label}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    );
}

function PhenologyTimeline({ hybrids, compact }: { hybrids: HybridPhenology[]; compact?: boolean }) {
    if (!hybrids.length) return null;
    return compact ? <CompactTimeline hybrids={hybrids} /> : <WideTimeline hybrids={hybrids} />;
}

export default PhenologyTimeline;
